import moment from 'moment-timezone'

// Pure helpers for the Cubbitos receiving screen (spec §6.1, §6.5, §5.4).

// The QR on the roll sticker encodes the RAW 16-char code (CBT + 8 digits + 5 chars);
// the sticker prints the same string. A hand-typed dashed/lowercase form must still
// resolve, and an empty scanner buffer must never reach the server.
export const normalizeRollCode = raw =>
  String(raw ?? '')
    .toUpperCase()
    .replace(/[\s-]/g, '')

export const looksLikeRollCode = value => /^CBT[0-9A-Z]{13}$/.test(value)

// Queue search: a roll code is matched EXACTLY server-side, so dashes must go; a
// receiver name must keep its spaces (ILIKE on first/last name), so only trim it.
export const normalizeSearchTerm = raw => {
  const trimmed = String(raw ?? '').trim()
  const asRoll = normalizeRollCode(trimmed)
  return looksLikeRollCode(asRoll) ? asRoll : trimmed
}

// label_file_url is a RELATIVE rails_blob_path (spec §3); an absolute URL is used as-is
// (the LabelView double-prefix bug, LabelView.js:17-23). The trailing slash a deploy can
// leave on REACT_APP_PLATFORM_URL is dropped: `…mx//rails/…` is a 404 on the floor.
export const buildLabelFileUrl = path => {
  if (!path) return null
  const value = String(path).trim()
  if (/^https?:\/\//i.test(value)) return value
  // Anything that is not a path is not made into one by prefixing it: `javascript:alert(1)`
  // would come back as a plausible-looking absolute url and pass the link guard below.
  if (!value.startsWith('/')) return null
  const base = String(process.env.REACT_APP_PLATFORM_URL ?? '').replace(/\/+$/, '')
  return `${base}${value}`
}

// Only an http(s) URL may be rendered as a link. `tracking_url` and `label_file_url` come
// from a carrier payload the WMS does not control; a `javascript:` or `data:` value would
// otherwise be one operator click away from running inside the session.
export const externalHttpUrl = value => {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return /^https?:\/\//i.test(trimmed) ? trimmed : null
}

export const formatArrival = (iso, timeZone) => {
  if (!iso) return '-'
  const m = timeZone ? moment(iso).tz(timeZone) : moment(iso)
  return m.isValid() ? m.format('DD/MM HH:mm') : '-'
}

export const getC2cErrorStatus = error => error?.response?.status ?? null

export const getC2cErrorCode = error => {
  const raw = error?.response?.data?.error
  if (typeof raw === 'string') return raw
  if (raw && typeof raw === 'object' && typeof raw.code === 'string') return raw.code
  return null
}

export const isForbiddenError = error => getC2cErrorStatus(error) === 403
export const isConflictError = error => getC2cErrorStatus(error) === 409

// "Procesados en esta sesión" strip: the only recovery path for a lost label response
// (a shipped order leaves the queue and re-scanning answers NOT_RECEIVABLE). sessionStorage
// so it survives a reload but not the tab; keyed by user AND warehouse (station PCs are
// shared, so one operator must not see the previous operator's recipients after a
// re-login); capped. `pushProcessed` is pure so a storage write failure can never lose
// the in-memory list the caller holds — the caller persists separately.
export const PROCESSED_LIMIT = 20

export const PROCESSED_KEY_PREFIX = 'c2c_receiving:processed:'

export const processedStorageKey = (userId, warehouseId) => `${PROCESSED_KEY_PREFIX}${userId ?? 'anon'}:${warehouseId}`

// Keying by user is not enough on a shared station: sessionStorage outlives a logout in the
// same tab, so the next operator would find the previous one's packages (and recipients)
// under their own key the moment the ids happened to line up. The logout handler wipes every
// strip this browser holds.
export const clearProcessed = () => {
  try {
    const storage = window.sessionStorage
    const keys = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key && key.startsWith(PROCESSED_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach(key => storage.removeItem(key))
  } catch (_) {
    // Private mode / storage disabled: there was nothing to clear.
  }
}

export const loadProcessed = key => {
  try {
    const raw = window.sessionStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter(item => item && item.order && typeof item.order.id === 'number') : []
  } catch (_) {
    return []
  }
}

// Pure: newest first, one entry per order, capped. The caller persists.
export const pushProcessed = (list, entry) => [entry, ...(list || []).filter(item => item.order.id !== entry.order.id)].slice(0, PROCESSED_LIMIT)

export const persistProcessed = (key, list) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(list))
  } catch (_) {
    // Private mode / quota: the in-memory list the caller holds is still the truth.
  }
}
